import { memo } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { cn } from "@/lib/utils";
import { MusicConfig } from "@/lib/lyria-client";

interface StemControlsProps {
  config: MusicConfig;
  onUpdateConfig: (config: Partial<MusicConfig>) => void;
}

interface StemButtonProps {
  label: string;
  muted: boolean;
  onToggle: () => void;
}

function StemButton({ label, muted, onToggle }: StemButtonProps) {
  const Icon = muted ? VolumeX : Volume2;
  return (
    <button
      onClick={onToggle}
      className={cn(
        "flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-all",
        muted
          ? "bg-white/5 text-muted-foreground"
          : "bg-white/10 text-foreground"
      )}
    >
      <Icon className="w-4 h-4" />
      <span className="text-[10px] uppercase tracking-wider">{label}</span>
    </button>
  );
}

export const StemControls = memo(function StemControls({
  config,
  onUpdateConfig,
}: StemControlsProps) {
  const drumsIsMuted = config.muteDrums ?? false;
  const bassIsMuted = config.muteBass ?? false;
  // "Other" is muted when onlyBassAndDrums is true
  const otherIsMuted = config.onlyBassAndDrums ?? false;

  return (
    <div className="flex items-center gap-2">
      <StemButton
        label="Drums"
        muted={drumsIsMuted}
        onToggle={() => onUpdateConfig({ muteDrums: !drumsIsMuted })}
      />
      <StemButton
        label="Bass"
        muted={bassIsMuted}
        onToggle={() => onUpdateConfig({ muteBass: !bassIsMuted })}
      />
      <StemButton
        label="Other"
        muted={otherIsMuted}
        onToggle={() => onUpdateConfig({ onlyBassAndDrums: !otherIsMuted })}
      />
    </div>
  );
});
